import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaClock } from 'react-icons/fa';
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/Home.css";
import Footer from "../components/Footer.jsx";
import { Button } from "react-bootstrap";
import { FaArrowRight } from "react-icons/fa";
import COLORS from "../styles/colors.js";

function Home() {
  const [allTalkshows, setAllTalkshows] = useState([]);
  const [filteredTalkshows, setFilteredTalkshows] = useState([]);
  const [featuredTalkshows, setFeaturedTalkshows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hotTopics, setHotTopics] = useState([]);
  const [currentSubtopicIndices, setCurrentSubtopicIndices] = useState({});
  const [categoriesMap, setCategoriesMap] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const searchTerm = searchParams.get('q') || '';
  const searchFilter = searchParams.get('filter') || '';

  // --- Helper functions ---
  const safeStringify = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const safeIncludes = (value, term) => {
    const sVal = safeStringify(value).toLowerCase();
    const sTerm = safeStringify(term).toLowerCase();
    return sVal.includes(sTerm);
  };

  const filterTalkshows = (talkshows, term, filter) => {
    if (!term && !filter) return talkshows;
    return talkshows.filter(talkshow => {
      const matchesText = !term || (
        safeIncludes(talkshow.title, term) ||
        safeIncludes(talkshow.description, term) ||
        safeIncludes(talkshow.category, term) ||
        (Array.isArray(talkshow.tags) && talkshow.tags.some(tag => safeIncludes(tag, term))) ||
        (Array.isArray(talkshow.subtopics) && talkshow.subtopics.some(st => safeIncludes(st.header_name, term) || safeIncludes(st.content, term))) ||
        safeIncludes(talkshow.host, term) ||
        (Array.isArray(talkshow.speakers) && talkshow.speakers.some(s => safeIncludes(s, term)))
      );

      const matchesFilter = !filter || (
        safeStringify(talkshow.category) === filter ||
        safeIncludes(talkshow.category, filter) ||
        (Array.isArray(talkshow.subtopics) && talkshow.subtopics.some(st => safeIncludes(st.header_name, filter) || safeIncludes(st.content, filter))) ||
        (Array.isArray(talkshow.tags) && talkshow.tags.some(tag => safeIncludes(tag, filter)))
      );

      return matchesText && matchesFilter;
    });
  };

  const highlightSearchTerm = (text, term) => {
    const sText = safeStringify(text);
    const sTerm = safeStringify(term);
    if (!sText || !sTerm) return sText;
    try {
      const regex = new RegExp(`(${sTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      return sText.replace(regex, '<span class="search-highlight">$1</span>');
    } catch {
      return sText;
    }
  };

  // --- Clean HTML before rendering ---
  // const cleanHTML = (text) => {
  //   if (!text) return '';
  //   return text
  //     .replace(/data-[^=]+="[^"]*"/g, '') // remove data-* attributes
  //     .replace(/<h[1-6][^>]*>/g, '') // remove inner heading tags
  //     .replace(/<\/h[1-6]>/g, '')
  //     .replace(/<p[^>]*>/g, '')
  //     .replace(/<\/p>/g, '')
  //     .trim();
  // };
  const cleanHTML = (text) => {
  if (!text) return '';

  // Decode HTML entities safely
  const div = document.createElement("div");
  div.innerHTML = text;

  // Get plain text
  const decoded = div.textContent || div.innerText || '';

  // Remove line breaks, collapse multiple spaces, and trim
  const cleaned = decoded.replace(/\s+/g, ' ').trim();
  
  // Capitalize first letter
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : '';
};


  // Rotate subtopics automatically
  useEffect(() => {
    if (!featuredTalkshows.length) return;
    const interval = setInterval(() => {
      setCurrentSubtopicIndices(prev => {
        const next = { ...prev };
        featuredTalkshows.forEach(s => {
          if (s.subtopics?.length > 1) {
            next[s.id] = (prev[s.id] || 0) + 1;
            next[s.id] = next[s.id] % s.subtopics.length;
          }
        });
        return next;
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [featuredTalkshows]);

  // Fetch talkshows
  useEffect(() => {
    const fetchTalkshows = async () => {
      try {
        setLoading(true);

        const base = (process.env.REACT_APP_BACKEND_API || '').trim();
        const baseUrl = base ? base.replace(/\/$/, '') : 'http://127.0.0.1:8000/api';

        // fetch categories and shows in parallel
        const [catsRes, showsRes] = await Promise.all([
          fetch(`${baseUrl}/categories/`),
          fetch(`${baseUrl}/shows/`)
        ]);

        // build categories map
        if (catsRes && catsRes.ok) {
          try {
            const catsData = await catsRes.json();
            const map = {};
            (catsData || []).forEach((c) => {
              const name = c.name || c.title || c.category_name || c.category || '';
              map[c.id] = name;
              map[String(c.id)] = name;
            });
            setCategoriesMap(map);
          } catch (e) {
            setCategoriesMap({});
          }
        }

        if (!showsRes.ok) throw new Error("Failed to fetch talk shows");
        const data = await showsRes.json();
        const publicTalkshows = data.filter(t => !t.is_private);
        setAllTalkshows(publicTalkshows);

        const filtered = filterTalkshows(publicTalkshows, searchTerm, searchFilter);
        setFilteredTalkshows(filtered);

        const talkshowsForFeatured = (searchTerm || searchFilter) && filtered.length ? filtered : publicTalkshows;
        if (talkshowsForFeatured.length) {
          const shuffled = [...talkshowsForFeatured].sort(() => 0.5 - Math.random());
          const featured = shuffled.slice(0, 8);
          setFeaturedTalkshows(featured);

          const initialInd = {};
          featured.forEach(f => (initialInd[f.id] = 0));
          setCurrentSubtopicIndices(initialInd);
        }

        // hot topics
        const talkshowsForTopics = (searchTerm || searchFilter) && filtered.length ? filtered : publicTalkshows;
        const allSubs = talkshowsForTopics.flatMap(t => t.subtopics?.map(s => ({ name: s.header_name, talkshowId: t.id })) || []);
        const unique = Array.from(new Map(allSubs.map(x => [x.name, x])).values()).slice(0, 6);
        setHotTopics(unique);

        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchTalkshows();
  }, [searchTerm, searchFilter]);

  const getUpcomingTalkshows = () => {
    const now = new Date();
    const talkshowsToFilter = (searchTerm || searchFilter) && filteredTalkshows.length ? filteredTalkshows : allTalkshows;
    return talkshowsToFilter.filter(t => new Date(`${t.show_date}T${t.start_time}`) > now).slice(0, 4);
  };

  const getCompletedTalkshows = () => {
    const now = new Date();
    const talkshowsToFilter = (searchTerm || searchFilter) && filteredTalkshows.length ? filteredTalkshows : allTalkshows;
    return talkshowsToFilter.filter(t => new Date(`${t.show_date}T${t.start_time}`) <= now).slice(0, 6);
  };

  const openArticle = (id) => navigate(`/read-more/${id}`);
  const clearSearch = () => navigate('/home');

  const getCategoryName = (talkshow) => {
    const catId = talkshow.category || talkshow.category_id || talkshow.categoryId || talkshow.category_id?.toString?.();
    if (!catId) return '';
    return categoriesMap[catId] || categoriesMap[String(catId)] || String(catId);
  };

  // --- BBC-style layout components ---
  // const TopStoriesBlock = () => {
  //   const main = featuredTalkshows[0];
  //   const leftStack = featuredTalkshows.slice(1, 5); // now 4 items

  //   const underMain = featuredTalkshows.slice(3, 5);
  //   return (
  //     <section className="top-stories">
  //       <div className="top-left">
  //         {leftStack.map(ts => {
  //           const sub = ts.subtopics?.[0];
  //           return (
  //             <article key={ts.id} className="small-tile" onClick={() => openArticle(ts.id)}>
  //               {sub?.image_urls?.[0] ? <img src={sub.image_urls[0]} alt={ts.title} /> : <div className="placeholder-img" />}
  //                 <div className="tile-head">
  //                   {getCategoryName(ts) && <span className="category-label">{getCategoryName(ts)}</span>}
  //                   <h4
  //                     dangerouslySetInnerHTML={{
  //                       __html: highlightSearchTerm(cleanHTML(ts.title), searchTerm),
  //                     }}
  //                   />
  //                 </div>
  //               <p className="meta">{ts.show_date}</p>
  //             </article>
  //           );
  //         })}
  //       </div>

  //       <div className="top-center">
  //         {main && (
  //           <article className="main-tile" onClick={() => openArticle(main.id)}>
  //             {main.subtopics?.[0]?.image_urls?.[0] ? (
  //               <img src={main.subtopics[0].image_urls[0]} alt={main.title} />
  //             ) : <div className="placeholder-main" />}

  //             <div className="main-text">
  //               <div className="tile-head">
  //                 {getCategoryName(main) && <span className="category-label">{getCategoryName(main)}</span>}
  //                 <h2 dangerouslySetInnerHTML={{ __html: highlightSearchTerm(cleanHTML(main.title), searchTerm) }} />
  //               </div>
  //               <p
  //                 className="excerpt"
  //                 dangerouslySetInnerHTML={{
  //                   __html: cleanHTML(main.subtopics?.[0]?.content?.substring(0, 160) ?? ''),
  //                 }}
  //               />
  //               <div className="meta-row">
  //                 <span className="meta"><FaCalendarAlt /> {main.show_date}</span>
  //                 <span className="meta"><FaClock /> {main.start_time}</span>
  //               </div>
  //             </div>
  //           </article>
  //         )}

  //         <div className="under-main">
  //           {underMain.map(ts => {
  //             const sub = ts.subtopics?.[0];
  //             return (
  //               <article className="mid-tile" key={ts.id} onClick={() => openArticle(ts.id)}>
  //                 {sub?.image_urls?.[0] ? <img src={sub.image_urls[0]} alt={ts.title} /> : <div className="placeholder-img" />}
  //                 <div className="mid-text">
  //                   <div className="tile-head">
  //                     {getCategoryName(ts) && <span className="category-label">{getCategoryName(ts)}</span>}
  //                     <h5
  //                       dangerouslySetInnerHTML={{
  //                         __html: highlightSearchTerm(cleanHTML(ts.title), searchTerm),
  //                       }}
  //                     />
  //                   </div>
  //                   <p className="meta">{ts.show_date}</p>
  //                 </div>
  //               </article>
  //             );
  //           })}
  //         </div>

  //       </div>

  //       <aside className="top-right">
  //         {/* 🔥 Hot Topics */}
  //         <div className="sidebar-card hot-topics-short">
  //           <h4>Hot Topics</h4>
  //           {loading ? (
  //             <p>Loading...</p>
  //           ) : hotTopics.length ? (
  //             <ul>
  //               {hotTopics.slice(0, 5).map((t, i) => (
  //                 <li
  //                   key={i}
  //                   onClick={() => openArticle(t.talkshowId)}
  //                   dangerouslySetInnerHTML={{
  //                     __html: highlightSearchTerm(cleanHTML(t.name), searchTerm),
  //                   }}
  //                 />
  //               ))}
  //             </ul>
  //           ) : (
  //             <p>No topics</p>
  //           )}
  //         </div>

  //         {/* 📅 Upcoming Shows */}
  //         <div className="sidebar-card small-list">
  //           <h4>Upcoming Shows</h4>
  //           {getUpcomingTalkshows().slice(0, 5).map((u) => (
  //             <div className="side-row" key={u.id} onClick={() => openArticle(u.id)}>
  //               <div className="side-text">
  //                 <div
  //                   className="side-title"
  //                   dangerouslySetInnerHTML={{
  //                     __html: highlightSearchTerm(cleanHTML(u.title), searchTerm),
  //                   }}
  //                 />
  //                 <div className="side-meta">{u.show_date}</div>
  //               </div>
  //             </div>
  //           ))}
  //           {getUpcomingTalkshows().length === 0 && (
  //             <div className="muted">No upcoming shows</div>
  //           )}
  //         </div>

  //         {/* ✅ Completed Shows */}
  //         <div className="sidebar-card small-list">
  //           <h4>Completed Shows</h4>
  //           {getCompletedTalkshows().slice(0, 5).map((c) => (
  //             <div className="side-row" key={c.id} onClick={() => openArticle(c.id)}>
  //               <div className="side-text">
  //                 <div
  //                   className="side-title"
  //                   dangerouslySetInnerHTML={{
  //                     __html: highlightSearchTerm(cleanHTML(c.title), searchTerm),
  //                   }}
  //                 />
  //                 <div className="side-meta">{c.show_date}</div>
  //               </div>
  //             </div>
  //           ))}
  //           {getCompletedTalkshows().length === 0 && (
  //             <div className="muted">No completed shows</div>
  //           )}
  //         </div>
  //       </aside>

  //     </section>
  //   );
  // };

  // --- TopStoriesBlock with no duplicate shows ---
const TopStoriesBlock = () => {
  const main = featuredTalkshows[0];
  const leftStack = featuredTalkshows.slice(1, 5); // indexes 1-4
  const usedIds = new Set([main?.id, ...leftStack.map(t => t.id)]);

  // Pick underMain from remaining shows after main + leftStack
  const underMain = featuredTalkshows.filter(t => !usedIds.has(t.id)).slice(0, 2);

  return (
    <section className="top-stories">
      <div className="top-left">
        {leftStack.map(ts => {
          const sub = ts.subtopics?.[0];
          return (
            <article key={ts.id} className="small-tile" onClick={() => openArticle(ts.id)}>
              {sub?.image_urls?.[0] ? <img src={sub.image_urls[0]} alt={ts.title} /> : <div className="placeholder-img" />}
              <div className="tile-head">
                {getCategoryName(ts) && <span className="category-label">{getCategoryName(ts)}</span>}
                <h4
                  dangerouslySetInnerHTML={{
                    __html: highlightSearchTerm(cleanHTML(ts.title), searchTerm),
                  }}
                />
              </div>
              <p className="meta">{ts.show_date}</p>
            </article>
          );
        })}
      </div>

      <div className="top-center">
        {main && (
          <article className="main-tile" onClick={() => openArticle(main.id)}>
            {main.subtopics?.[0]?.image_urls?.[0] ? (
              <img src={main.subtopics[0].image_urls[0]} alt={main.title} />
            ) : <div className="placeholder-main" />}

            <div className="main-text">
              <div className="tile-head">
                {getCategoryName(main) && <span className="category-label">{getCategoryName(main)}</span>}
                <h2 dangerouslySetInnerHTML={{ __html: highlightSearchTerm(cleanHTML(main.title), searchTerm) }} />
              </div>
              <p
                className="excerpt"
                dangerouslySetInnerHTML={{
                  __html: cleanHTML(main.subtopics?.[0]?.content?.substring(0, 160) ?? ''),
                }}
              />
              <div className="meta-row">
                <span className="meta"><FaCalendarAlt /> {main.show_date}</span>
                <span className="meta"><FaClock /> {main.start_time}</span>
              </div>
            </div>
          </article>
        )}

        <div className="under-main">
          {underMain.map(ts => {
            const sub = ts.subtopics?.[0];
            return (
              <article className="mid-tile" key={ts.id} onClick={() => openArticle(ts.id)}>
                {sub?.image_urls?.[0] ? <img src={sub.image_urls[0]} alt={ts.title} /> : <div className="placeholder-img" />}
                <div className="mid-text">
                  <div className="tile-head">
                    {getCategoryName(ts) && <span className="category-label">{getCategoryName(ts)}</span>}
                    <h5
                      dangerouslySetInnerHTML={{
                        __html: highlightSearchTerm(cleanHTML(ts.title), searchTerm),
                      }}
                    />
                  </div>
                  <p className="meta">{ts.show_date}</p>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <aside className="top-right">
        {/*Hot Topics */}
        <div className="sidebar-card hot-topics-short">
          <h4>Hot Topics</h4>
          {loading ? (
            <p>Loading...</p>
          ) : hotTopics.length ? (
            <ul>
              {hotTopics.slice(0, 5).map((t, i) => (
                <li
                  key={i}
                  onClick={() => openArticle(t.talkshowId)}
                  dangerouslySetInnerHTML={{
                    __html: highlightSearchTerm(cleanHTML(t.name), searchTerm),
                  }}
                />
              ))}
            </ul>
          ) : (
            <p>No topics</p>
          )}
        </div>

        {/*Upcoming Shows */}
        <div className="sidebar-card small-list">
          <h4>Upcoming Shows</h4>
          {getUpcomingTalkshows().slice(0, 5).map((u) => (
            <div className="side-row" key={u.id} onClick={() => openArticle(u.id)}>
              <div className="side-text">
                <div
                  className="side-title"
                  dangerouslySetInnerHTML={{
                    __html: highlightSearchTerm(cleanHTML(u.title), searchTerm),
                  }}
                />
                <div className="side-meta">{u.show_date}</div>
              </div>
            </div>
          ))}
          {getUpcomingTalkshows().length === 0 && (
            <div className="muted">No upcoming shows</div>
          )}
        </div>

        {/*Completed Shows */}
        <div className="sidebar-card small-list">
          <h4>Completed Shows</h4>
          {getCompletedTalkshows().slice(0, 5).map((c) => (
            <div className="side-row" key={c.id} onClick={() => openArticle(c.id)}>
              <div className="side-text">
                <div
                  className="side-title"
                  dangerouslySetInnerHTML={{
                    __html: highlightSearchTerm(cleanHTML(c.title), searchTerm),
                  }}
                />
                <div className="side-meta">{c.show_date}</div>
              </div>
            </div>
          ))}
          {getCompletedTalkshows().length === 0 && (
            <div className="muted">No completed shows</div>
          )}
        </div>
      </aside>
    </section>
  );
};


  const MoreStoriesGrid = () => {
    // Get IDs already displayed in TopStoriesBlock
    const main = featuredTalkshows[0];
    const leftStack = featuredTalkshows.slice(1, 5);
    const usedIds = new Set([main?.id, ...leftStack.map(t => t.id)]);
    const underMain = featuredTalkshows.filter(t => !usedIds.has(t.id)).slice(0, 2);
    underMain.forEach(t => usedIds.add(t.id));
    
    // Get completed and upcoming shows that aren't already displayed
    const now = new Date();
    const talkshowsToFilter = (searchTerm || searchFilter) && filteredTalkshows.length ? filteredTalkshows : allTalkshows;
    
    const completedShows = talkshowsToFilter
      .filter(t => new Date(`${t.show_date}T${t.start_time}`) <= now && !usedIds.has(t.id));
    
    const upcomingShows = talkshowsToFilter
      .filter(t => new Date(`${t.show_date}T${t.start_time}`) > now && !usedIds.has(t.id));
    
    // Combine and limit to 4 shows
    const more = [...completedShows, ...upcomingShows].slice(0, 4);
    
    if (!more.length) return null;
    return (
      <section className="more-stories">
        {more.map(t => {
          const sub = t.subtopics?.[0];
          return (
            <article className="grid-tile" key={t.id} onClick={() => openArticle(t.id)}>
              {sub?.image_urls?.[0] ? <img src={sub.image_urls[0]} alt={t.title} /> : <div className="placeholder-img" />}
              <div className="grid-text">
                  <div className="tile-head">
                    {getCategoryName(t) && <span className="category-label">{getCategoryName(t)}</span>}
                    <h4 dangerouslySetInnerHTML={{ __html: highlightSearchTerm(cleanHTML(t.title), searchTerm) }} />
                  </div>
                <p className="meta">{t.show_date}</p>
              </div>
            </article>
          );
        })}
      </section>
    );
  };

  // --- Main render ---
  return (
    <div className="home-wrapper bbc-homepage">
      <div className="hero container text-center" style={{ marginTop: "60px", marginBottom: "60px" }}>
        <h1 style={{ fontSize: "36px", fontWeight: "bold", color: COLORS.primary }}>
          Welcome to Talk Show
        </h1>
        <p style={{ fontSize: "20px", color: COLORS.text_muted, maxWidth: "800px", margin: "8px auto" }}>
          Your gateway to exploring the world of ideas through engaging talk shows,
          thought-provoking discussions, and AI-powered insights.
        </p>
        <div className="CTA-Buttons">
          <Button className="explore-btn me-3" style={{ fontSize: "14px", height: "36px" }} onClick={() => navigate("/news")}>
            Explore Talk Shows <FaArrowRight style={{ marginLeft: "8px" }} />
          </Button>
          <Button
            style={{
              color: COLORS.primary,
              backgroundColor: COLORS.white,
              border: `1px solid ${COLORS.border}`,
              fontSize: "14px",
              fontWeight: "500",
              height: "36px",
            }}
            onClick={() => navigate("/chat-bot")}
          >
            Try AI Assistant
          </Button>
        </div>
      </div>

      {(searchTerm || searchFilter) && (
        <div className="search-banner">
          <div>
            <strong>Search Results</strong>
            {searchTerm && ` for "${searchTerm}"`}
            {searchFilter && ` in ${searchFilter}`}
          </div>
          <div className="result-count">{filteredTalkshows.length} results</div>
          <button className="clear-search-btn" onClick={clearSearch}>Clear</button>
        </div>
      )}

      {loading ? <div className="loading-block">Loading...</div> : error ? <div className="error-block">{error}</div> : <TopStoriesBlock />}
      <MoreStoriesGrid />
    </div>
  );
}

export default Home;
